/** Cotizador en pantalla unica: compone un requerimiento nuevo (design.md §22.5). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { MobileLayout } from '@/components/MobileLayout';
import { MobileButton } from '@/components/MobileButton';
import { MobileIcon } from '@/components/MobileIcon';
import { MobileInput } from '@/components/MobileInput';
import { ClientAutocomplete } from '@/components/ClientAutocomplete';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { QuantityStepper } from '@/components/QuantityStepper';
import { SearchInput } from '@/components/SearchInput';
import { SectionCard } from '@/components/SectionCard';
import { CardListSkeleton } from '@/components/LoadingSkeleton';
import { EmptyState } from '@/components/EmptyState';
import {
  useCatalogCompositionQuery,
  useComposeRequirementMutation,
  useMobileCatalogQuery,
  useSimulateQuoteMutation,
  type CatalogGroup,
} from '@/hooks/useMobileQuotations';
import {
  mobileQuoteGeneratorSchema,
  type MobileQuoteGeneratorForm,
} from '@/schemas/quote-generator.schemas';
import { extractErrorMessage, newIdempotencyKey } from '@/services/mobileApiClient';
import type {
  CatalogItemDto,
  CompositionNode,
  SimulationResultDto,
} from '@/services/commercialService';
import { useMonetaryPrecision } from '@/state/MonetaryPrecisionContext';
import { hapticSuccess } from '@/utils/haptic';

const CATALOG_GROUPS = [
  { value: 'sale', label: 'Partidas y Modelos' },
  { value: 'additional', label: 'Artículos de venta' },
] as const;

const MIN_SEARCH_LENGTH = 2;

type SelectedLine = {
  item: CatalogItemDto;
  quantity: number;
  group: CatalogGroup;
};

function todayIso(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatQuantity(value: unknown): string | null {
  const parsed = toNumber(value);
  if (parsed === null) return null;
  return parsed.toLocaleString('es-PE', { maximumFractionDigits: 4 });
}

function formatDate(value: string | undefined): string {
  if (!value) return '—';
  const date = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function CompositionChildren({ nodes, depth }: { nodes: CompositionNode[]; depth: number }) {
  if (nodes.length === 0) return null;
  return (
    <ul className="mt-2 space-y-1.5">
      {nodes.map((child, index) => {
        const key = child.master_item_id ?? child.code ?? `nodo-${depth}-${index}`;
        const quantity = formatQuantity(child.quantity_total);
        const meta = [quantity, child.unit_code].filter((part) => part !== null && part !== undefined).join(' ');
        return (
          <li key={key} className="rounded-lg bg-white px-3 py-2">
            <div className="flex items-start justify-between gap-3">
              <p className="min-w-0 flex-1 break-words text-sm font-medium text-text-primary">
                {child.name ?? 'Componente'}
              </p>
              {meta !== '' && (
                <span className="shrink-0 text-sm tabular-nums text-text-secondary">{meta}</span>
              )}
            </div>
            <CompositionChildren nodes={child.children ?? []} depth={depth + 1} />
          </li>
        );
      })}
    </ul>
  );
}

function CompositionContent({ itemId, quantity }: { itemId: string; quantity: number }) {
  const compositionQuery = useCatalogCompositionQuery(itemId, quantity);
  if (compositionQuery.isPending) {
    return <p className="mt-2 truncate text-sm text-text-tertiary">Cargando contenido…</p>;
  }
  if (compositionQuery.isError) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <MobileIcon name="error" className="h-4 w-4 shrink-0 text-error" />
        <p className="min-w-0 flex-1 truncate text-sm text-error">No se pudo cargar el contenido.</p>
        <button
          type="button"
          onClick={() => void compositionQuery.refetch()}
          className="flex min-h-[44px] shrink-0 items-center rounded-lg px-2 text-sm font-semibold text-error focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Reintentar
        </button>
      </div>
    );
  }
  const children = compositionQuery.data?.children ?? [];
  if (children.length === 0) {
    return <p className="mt-2 truncate text-sm text-text-tertiary">Sin componentes.</p>;
  }
  return <CompositionChildren nodes={children} depth={0} />;
}

function CatalogItemCard({
  item,
  quantity,
  expanded,
  onQuantityChange,
  onToggleComposition,
}: {
  item: CatalogItemDto;
  quantity: number;
  expanded: boolean;
  onQuantityChange: (item: CatalogItemDto, quantity: number) => void;
  onToggleComposition: () => void;
}) {
  const precision = useMonetaryPrecision();
  return (
    <div
      className={clsx(
        'rounded-xl px-3.5 py-3 transition-colors',
        quantity > 0 ? 'bg-primary-light ring-1 ring-primary' : 'bg-bg-tertiary',
      )}
    >
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p
            title={item.name}
            className="line-clamp-2 break-words font-semibold leading-5 text-text-primary"
          >
            {item.name}
          </p>
          {item.price !== undefined && (
            <p className="mt-1 truncate text-sm text-text-secondary">
              {precision.format('quotation_items', 'unit_price', item.price, item.currency_code ?? 'PEN')}
              {item.unit_code ? (
                <span className="text-text-tertiary"> · {item.unit_code}</span>
              ) : null}
            </p>
          )}
        </div>
        <div className="shrink-0">
          <QuantityStepper
            label={item.name}
            value={quantity}
            onChange={(next) => onQuantityChange(item, next)}
            compact
          />
        </div>
      </div>
      {item.has_composition && (
        <div className="mt-1">
          <button
            type="button"
            onClick={onToggleComposition}
            aria-expanded={expanded}
            className="flex min-h-[44px] items-center gap-1 text-sm font-semibold text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <MobileIcon name={expanded ? 'chevron_down' : 'chevron_right'} className="h-5 w-5" />
            {expanded ? 'Ocultar contenido' : 'Ver contenido'}
          </button>
          {expanded && <CompositionContent itemId={item.id} quantity={quantity > 0 ? quantity : 1} />}
        </div>
      )}
    </div>
  );
}

export function QuoteGeneratorPage() {
  const navigate = useNavigate();
  const precision = useMonetaryPrecision();
  const [itemSearch, setItemSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [group, setGroup] = useState<CatalogGroup>('sale');
  const [selection, setSelection] = useState<Record<string, SelectedLine>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [totalsExpanded, setTotalsExpanded] = useState(false);
  const totalsSwipeStart = useRef<number | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [simulation, setSimulation] = useState<SimulationResultDto | null>(null);
  const [simulatedKey, setSimulatedKey] = useState<string | null>(null);
  const [simulationError, setSimulationError] = useState<string | null>(null);
  const [debouncedSimulationKey, setDebouncedSimulationKey] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState('');
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [pendingBody, setPendingBody] = useState<Record<string, unknown> | null>(null);
  const [pendingIdempotencyKey, setPendingIdempotencyKey] = useState<string | null>(null);
  const catalogQuery = useMobileCatalogQuery(debouncedSearch, group);
  const simulateMutation = useSimulateQuoteMutation();
  const composeMutation = useComposeRequirementMutation();
  const {
    register,
    control,
    watch,
    setValue,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<MobileQuoteGeneratorForm>({
    resolver: yupResolver(mobileQuoteGeneratorSchema),
    defaultValues: {
      client_id: '',
      new_requirement_name: '',
      new_requirement_required_date: '',
      lines: [],
      discount_percent: 0,
      discount_reason: '',
    },
  });

  const clientId = watch('client_id') ?? '';
  const discountPercent = Number(watch('discount_percent') ?? 0) || 0;
  const items = useMemo(() => catalogQuery.data ?? [], [catalogQuery.data]);
  const searchActive = debouncedSearch.trim().length >= MIN_SEARCH_LENGTH;
  const currentLines = useMemo(
    () =>
      Object.values(selection)
        .map((line) => ({ master_item_id: line.item.id, quantity: line.quantity }))
        .sort((a, b) => a.master_item_id.localeCompare(b.master_item_id)),
    [selection],
  );
  const groupLines = useMemo(
    () => Object.values(selection).filter((line) => line.group === group),
    [selection, group],
  );
  const totalItems = currentLines.length;
  const hasLines = currentLines.length > 0;
  const busy = simulateMutation.isPending || composeMutation.isPending;
  const currentSimulationKey = useMemo(
    () => JSON.stringify({ lines: currentLines, discount_percent: discountPercent }),
    [currentLines, discountPercent],
  );
  const currentSimulationKeyRef = useRef(currentSimulationKey);
  const stale = simulation !== null && simulatedKey !== currentSimulationKey;
  const calculationReady =
    simulation !== null &&
    simulatedKey === currentSimulationKey &&
    !simulateMutation.isPending &&
    simulationError === null;
  const calculationLabel = simulateMutation.isPending || stale ? 'Calculando…' : '—';

  useEffect(() => {
    currentSimulationKeyRef.current = currentSimulationKey;
  }, [currentSimulationKey]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(itemSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [itemSearch]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSimulationKey(currentSimulationKey), 300);
    return () => clearTimeout(timer);
  }, [currentSimulationKey]);

  useEffect(() => {
    let cancelled = false;
    if (currentLines.length === 0) {
      setSimulation(null);
      setSimulatedKey(null);
      setSimulationError(null);
      return;
    }
    const raw = JSON.parse(debouncedSimulationKey) as {
      lines: { master_item_id: string; quantity: number }[];
      discount_percent: number;
    };
    if (raw.lines.length === 0) return;
    const seal = debouncedSimulationKey;
    simulateMutation
      .mutateAsync({ lines: raw.lines, discount_percent: raw.discount_percent })
      .then((result) => {
        if (cancelled) return;
        setSimulation(result);
        setSimulatedKey(seal);
        setSimulationError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSimulation(null);
        setSimulatedKey(null);
        setSimulationError(extractErrorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSimulationKey]);

  useEffect(() => {
    setValue('lines', currentLines, { shouldValidate: false });
  }, [currentLines, setValue]);

  useEffect(() => {
    if (clientId === '') setSelectedClientName('');
  }, [clientId]);

  const groupSubtotal = useMemo(() => {
    if (!calculationReady || simulation === null) return null;
    return simulation.lines.reduce((sum, line) => {
      const selected = selection[line.master_item_id];
      if (selected?.group !== group) return sum;
      return sum + (toNumber(line.line_total) ?? 0);
    }, 0);
  }, [calculationReady, simulation, selection, group]);

  const subtotal = calculationReady && simulation ? toNumber(simulation.subtotal) : null;
  const discountAmount = calculationReady && simulation ? toNumber(simulation.discount_amount) : null;
  const total = calculationReady && simulation ? toNumber(simulation.total_amount) : null;

  function pickGroup(next: CatalogGroup) {
    if (next === group) return;
    setGroup(next);
    setItemSearch('');
    setDebouncedSearch('');
  }

  function changeQuantity(item: CatalogItemDto, quantity: number) {
    setSelection((previous) => {
      const next = { ...previous };
      if (quantity <= 0) {
        delete next[item.id];
      } else {
        next[item.id] = { item, quantity, group };
      }
      return next;
    });
  }

  async function retrySimulation() {
    if (currentLines.length === 0) return;
    const seal = currentSimulationKey;
    setSimulationError(null);
    try {
      const result = await simulateMutation.mutateAsync({
        lines: currentLines,
        discount_percent: discountPercent,
      });
      if (currentSimulationKeyRef.current !== seal) return;
      setSimulation(result);
      setSimulatedKey(seal);
    } catch (error) {
      if (currentSimulationKeyRef.current !== seal) return;
      setSimulation(null);
      setSimulatedKey(null);
      setSimulationError(extractErrorMessage(error));
    }
  }

  async function onSubmit(values: MobileQuoteGeneratorForm) {
    setGeneralError(null);
    setConfirmError(null);
    if (!calculationReady || simulation === null) {
      setGeneralError('Espera el cálculo del total para guardar.');
      return;
    }
    const discount = Number(values.discount_percent) || 0;
    const body: Record<string, unknown> = {
      client_id: values.client_id,
      new_requirement: {
        name: values.new_requirement_name.trim(),
        required_date: values.new_requirement_required_date,
      },
      lines: values.lines,
      discount_percent: discount,
    };
    if (discount > 0) body['discount_reason'] = values.discount_reason?.trim();
    const signature = JSON.stringify(body);
    const sameIntent =
      pendingBody !== null && JSON.stringify(pendingBody) === signature && pendingIdempotencyKey !== null;
    let key: string;
    try {
      key = sameIntent && pendingIdempotencyKey ? pendingIdempotencyKey : newIdempotencyKey();
    } catch {
      setGeneralError('No se pudo preparar el guardado. Intenta nuevamente.');
      return;
    }
    setPendingBody(body);
    setPendingIdempotencyKey(key);
    const totalAmount = toNumber(simulation.total_amount);
    const summary = [
      `Cliente: ${selectedClientName || 'seleccionado'}`,
      `Requerimiento: ${values.new_requirement_name.trim()} (${formatDate(values.new_requirement_required_date)})`,
      `${values.lines.length} partida${values.lines.length !== 1 ? 's' : ''}`,
      `Total: ${totalAmount !== null ? precision.format('quotations', 'total', totalAmount, simulation.currency) : '—'}`,
    ].join(' · ');
    setConfirmSummary(summary);
    setConfirmError(null);
    setConfirmOpen(true);
  }

  function onInvalid() {
    setGeneralError('Revisa los campos obligatorios para generar el requerimiento.');
    requestAnimationFrame(() => {
      document.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
    });
  }

  async function confirmCompose() {
    if (!pendingBody || !pendingIdempotencyKey || composeMutation.isPending) return;
    setGeneralError(null);
    setConfirmError(null);
    try {
      const created = await composeMutation.mutateAsync({
        body: pendingBody,
        idempotencyKey: pendingIdempotencyKey,
      });
      setSelection({});
      setSimulation(null);
      setSimulatedKey(null);
      setSimulationError(null);
      setItemSearch('');
      setDebouncedSearch('');
      setGroup('sale');
      setExpandedId(null);
      setTotalsExpanded(false);
      setPendingBody(null);
      setPendingIdempotencyKey(null);
      setConfirmOpen(false);
      reset();
      hapticSuccess();
      toast.success(`Requerimiento ${created.code || created.id} creado`);
      navigate(`/m/requirements/${created.id}`, { replace: true });
    } catch (err) {
      setConfirmError(extractErrorMessage(err));
    }
  }

  const results = items;
  const priceText = (value: number | null, field: 'subtotal' | 'discount' | 'total') =>
    value === null
      ? '—'
      : precision.format('quotations', field, value, simulation?.currency ?? 'PEN');

  return (
    <MobileLayout title="Cotizador" subtitle={`${currentLines.length} partidas con cantidad`}>
      <form onSubmit={(e) => void handleSubmit(onSubmit, onInvalid)(e)} className="space-y-4 pb-64" noValidate>
        <SectionCard title="Cliente y requerimiento nuevo">
          <div className="space-y-4">
            <Controller
              control={control}
              name="client_id"
              render={({ field }) => (
                <ClientAutocomplete
                  value={field.value || null}
                  onChange={(next, client) => {
                    field.onChange(next ?? '');
                    setSelectedClientName(client?.legal_name ?? '');
                  }}
                  label="Cliente"
                  isRequired
                  disabled={busy}
                  error={errors.client_id?.message}
                />
              )}
            />

            <MobileInput
              label="Nombre del requerimiento"
              placeholder="Ej: Servicio de mantenimiento"
              enterKeyHint="next"
              staticLabel
              isRequired
              icon={<MobileIcon name="title" className="h-5 w-5" />}
              error={errors.new_requirement_name?.message}
              {...register('new_requirement_name')}
            />
            <MobileInput
              label="Fecha requerida"
              type="date"
              staticLabel
              isRequired
              min={todayIso()}
              icon={<MobileIcon name="schedule" className="h-5 w-5" />}
              error={errors.new_requirement_required_date?.message}
              {...register('new_requirement_required_date')}
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Partidas"
          subtitle={group === 'sale' ? 'Partidas y Modelos' : 'Artículos de venta'}
        >
          <div
            role="radiogroup"
            aria-label="Grupo del catálogo"
            className="mb-3 inline-flex w-full gap-1 rounded-full bg-bg-tertiary p-1"
          >
            {CATALOG_GROUPS.map((option) => {
              const active = group === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={busy}
                  onClick={() => pickGroup(option.value)}
                  className={clsx(
                    'flex min-h-[44px] flex-1 items-center justify-center rounded-full px-3 text-sm font-semibold transition-colors disabled:opacity-40',
                    active ? 'bg-white text-primary shadow-card' : 'text-text-secondary',
                  )}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <SearchInput
            value={itemSearch}
            onChange={setItemSearch}
            placeholder="Buscar por código o nombre..."
          />
          {errors.lines?.message && (
            <p className="mt-1.5 flex items-center gap-1 text-sm text-error">
              <MobileIcon name="error" className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 break-words">{errors.lines.message as string}</span>
            </p>
          )}

          <div id="quote-catalog-results" className="mt-3 space-y-3">
            {searchActive && catalogQuery.isPending && <CardListSkeleton count={3} />}
            {searchActive && catalogQuery.isError && (
              <EmptyState
                icon="error"
                title="No se pudo cargar el catálogo"
                description="Verifica tu conexión e intenta de nuevo."
                actionLabel="Reintentar"
                onAction={() => catalogQuery.refetch()}
              />
            )}
            {searchActive && !catalogQuery.isPending && !catalogQuery.isError && results.length === 0 && (
              <EmptyState
                icon="inventory_2"
                title="Sin resultados"
                description="Ninguno coincide con tu búsqueda."
              />
            )}
            {searchActive &&
              results.map((item) => (
                <CatalogItemCard
                  key={item.id}
                  item={item}
                  quantity={selection[item.id]?.quantity ?? 0}
                  expanded={expandedId === item.id}
                  onQuantityChange={changeQuantity}
                  onToggleComposition={() =>
                    setExpandedId((current) => (current === item.id ? null : item.id))
                  }
                />
              ))}
          </div>

          {!searchActive && groupLines.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-tertiary">
                Agregadas ({groupLines.length})
              </p>
              {groupLines.map((line) => (
                <CatalogItemCard
                  key={line.item.id}
                  item={line.item}
                  quantity={line.quantity}
                  expanded={expandedId === line.item.id}
                  onQuantityChange={changeQuantity}
                  onToggleComposition={() =>
                    setExpandedId((current) => (current === line.item.id ? null : line.item.id))
                  }
                />
              ))}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between gap-2 border-t border-border-light pt-3">
            <span className="text-sm font-medium text-text-secondary">
              Subtotal {group === 'sale' ? 'Partidas y Modelos' : 'Artículos de venta'}
            </span>
            <span className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">
              {groupLines.length === 0
                ? '—'
                : calculationReady
                  ? priceText(groupSubtotal, 'subtotal')
                  : calculationLabel}
            </span>
          </div>
        </SectionCard>

        <SectionCard title="Descuento">
          <div className="space-y-3">
            <MobileInput
              label="Descuento %"
              type="text"
              inputMode="decimal"
              placeholder="0"
              staticLabel
              icon={<MobileIcon name="calculate" className="h-5 w-5" />}
              error={errors.discount_percent?.message}
              {...register('discount_percent')}
            />
            {discountPercent > 0 && (
              <div className="flex items-center justify-between gap-2 rounded-xl bg-bg-tertiary px-3 py-2">
                <span className="text-sm text-text-secondary">Monto del descuento</span>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-text-primary">
                  {calculationReady ? priceText(discountAmount, 'discount') : calculationLabel}
                </span>
              </div>
            )}
            <MobileInput
              label={`Motivo del descuento${discountPercent > 0 ? ' *' : ''}`}
              placeholder="Ej: Pronto pago"
              enterKeyHint="done"
              staticLabel
              icon={<MobileIcon name="edit_note" className="h-5 w-5" />}
              error={errors.discount_reason?.message}
              {...register('discount_reason')}
            />
          </div>
        </SectionCard>

        {simulationError && (
          <div role="alert" className="flex items-center gap-2 rounded-2xl bg-error-light px-4 py-3">
            <MobileIcon name="error" className="h-5 w-5 shrink-0 text-error" />
            <p className="min-w-0 flex-1 break-words text-sm text-error">{simulationError}</p>
            <button
              type="button"
              onClick={() => void retrySimulation()}
              className="flex min-h-[44px] shrink-0 items-center rounded-lg px-2 text-sm font-semibold text-error"
            >
              Reintentar
            </button>
          </div>
        )}
      </form>

      {hasLines && (
        <div className="fixed inset-x-0 bottom-[76px] z-30 rounded-t-card border-t border-border-light bg-white shadow-card safe-area-bottom">
          <button
            type="button"
            aria-expanded={totalsExpanded}
            aria-controls="quote-totals-detail"
            onPointerDown={(event) => {
              totalsSwipeStart.current = event.clientY;
              event.currentTarget.setPointerCapture?.(event.pointerId);
            }}
            onPointerUp={(event) => {
              const start = totalsSwipeStart.current;
              totalsSwipeStart.current = null;
              if (start === null) return;
              const delta = start - event.clientY;
              if (Math.abs(delta) >= 24) {
                setTotalsExpanded(delta > 0);
              } else {
                setTotalsExpanded((current) => !current);
              }
            }}
            onClick={(event) => {
              if (event.detail === 0) setTotalsExpanded((current) => !current);
            }}
            className="w-full touch-none px-4 pb-2 pt-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            <span className="mx-auto mb-2 block h-1 w-10 rounded-full bg-border-dark" />
            <span className="flex items-center justify-between gap-2">
              <span className="font-semibold text-text-primary">Total general</span>
              <span className="flex items-center gap-2">
                <span
                  aria-live="polite"
                  className="shrink-0 text-xl font-bold tabular-nums text-primary"
                >
                  {calculationReady ? priceText(total, 'total') : calculationLabel}
                </span>
                <MobileIcon
                  name="chevron_down"
                  className={clsx(
                    'h-5 w-5 shrink-0 text-text-tertiary transition-transform',
                    !totalsExpanded && 'rotate-180',
                  )}
                />
              </span>
            </span>
          </button>

          <div
            id="quote-totals-detail"
            hidden={!totalsExpanded}
            className="space-y-1.5 px-4 pb-2"
          >
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-text-secondary">Subtotal general</span>
              <span className="shrink-0 font-medium tabular-nums text-text-primary">
                {calculationReady ? priceText(subtotal, 'subtotal') : calculationLabel}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-text-secondary">
                Descuento{discountPercent > 0 ? ` (${discountPercent} %)` : ''}
              </span>
              <span className="shrink-0 font-medium tabular-nums text-text-primary">
                {calculationReady ? priceText(discountAmount, 'discount') : calculationLabel}
              </span>
            </div>
          </div>

          <div className="space-y-2 px-4 pb-3">
            <p className="truncate text-xs text-text-tertiary">
              {totalItems} partida{totalItems !== 1 ? 's' : ''} con cantidad
            </p>
            {generalError && (
              <div role="alert" className="flex items-center gap-2 rounded-2xl bg-error-light px-3 py-2">
                <MobileIcon name="error" className="h-4 w-4 shrink-0 text-error" />
                <p className="min-w-0 flex-1 break-words text-sm text-error">{generalError}</p>
              </div>
            )}
            <MobileButton
              variant="primary"
              size="md"
              disabled={busy || !calculationReady}
              loading={composeMutation.isPending}
              onClick={() => void handleSubmit(onSubmit, onInvalid)()}
            >
              Guardar
            </MobileButton>
          </div>
        </div>
      )}

      {confirmOpen && (
        <ConfirmDialog
          title="Generar requerimiento"
          message={confirmSummary}
          confirmLabel="Generar RQ"
          busy={composeMutation.isPending}
          error={confirmError}
          onConfirm={() => void confirmCompose()}
          onCancel={() => {
            setConfirmOpen(false);
            setConfirmError(null);
            setPendingBody(null);
            setPendingIdempotencyKey(null);
          }}
        />
      )}
    </MobileLayout>
  );
}
